<template>
  <div v-if="userInfoLoaded">
    <b-card no-body class="card-container">
      <!-- Overlays s'il y a un isLoading -->
      <div v-if="isLoading">
        <div class="overlay show"></div>
        <div class="spanner show">
          <div class="loader"></div>
          <p>Updating passport owner...</p>
        </div>
      </div>

      <!-- HEADER -->
      <template #header>
        <b-row class="mb-2 mt-2">
          <!-- Col de gauche : Search widget -->
          <b-col md="6">
            <b-input-group class="search-widget">
              <template #prepend>
                <b-input-group-text>
                  <font-awesome-icon icon="search" />
                </b-input-group-text>
              </template>
              <b-form-input
                type="search"
                :placeholder="$t('search_passport')"
                v-model="searchText"
              />
              <template #append>
                <b-input-group-append>
                  <b-button
                    variant="outline-secondary"
                    @click="showFilters = !showFilters"
                  >
                    <font-awesome-icon icon="sliders-h" />
                    {{ $t("advanced_search") }}
                  </b-button>
                </b-input-group-append>
              </template>
            </b-input-group>

            <!-- Bloc de filtres avancés -->
            <div
              class="reports-filters card p-3 bg-gradient-light mt-3"
              v-show="showFilters"
            >
              <b-row>
                <b-col>
                  <!-- Status principal -->
                  <b-form-select
                    class="mb-2"
                    v-model="searchStatus"
                  >
                    <template #first>
                      <option value="">
                        {{ $t("Select status") }}
                      </option>
                    </template>
                    <option value="pending activation">Pending Activation</option>
                    <option value="active">Active</option>
                    <option value="suspended">Suspended</option>
                    <option value="revoked">Revoked</option>
                  </b-form-select>

                  <!-- Owner status -->
                  <b-form-select
                    class="mb-2"
                    v-model="searchOwnerStatus"
                  >
                    <template #first>
                      <option value="">
                        {{ $t("Owner status") }}
                      </option>
                    </template>
                    <option value="free">Free</option>
                    <option value="owned">Owned</option>
                  </b-form-select>
                </b-col>

                <b-col>
                  <!-- Client filter -->
                  <b-form-select
                    v-if="is_show_clients"
                    class="mb-2"
                    v-model="searchClient"
                  >
                    <template #first>
                      <option :value="null">
                        {{ $t("Client") }}
                      </option>
                    </template>
                    <option
                      v-for="client in clients"
                      :key="client.id"
                      :value="client.id"
                    >
                      {{ client.name }}
                    </option>
                  </b-form-select>

                  <!-- Manufacturer filter -->
                  <b-form-select
                    v-if="is_show_manufacturers"
                    class="mb-2"
                    v-model="searchManufacturer"
                  >
                    <template #first>
                      <option :value="null">
                        {{ $t("Manufacturer") }}
                      </option>
                    </template>
                    <option
                      v-for="manufacturer in manufacturers"
                      :key="manufacturer.id"
                      :value="manufacturer.id"
                    >
                      {{ manufacturer.name }}
                    </option>
                  </b-form-select>
                </b-col>
              </b-row>

              <!-- Boutons clear (plus de "Filter passports") -->
              <b-row class="mt-2">
                <b-col class="text-right">
                  <b-button
                    v-if="isFiltered"
                    variant="outline-secondary"
                    @click="resetFiltering"
                    class="mr-2"
                  >
                    {{ $t("clear") }}
                  </b-button>
                </b-col>
              </b-row>
            </div>
          </b-col>

          <!-- Col de droite -->
          <b-col md="6" class="text-right">
            <!-- (si besoin d'un bouton "Create new passport") -->
          </b-col>
        </b-row>
      </template>

      <!-- BODY -->
      <b-card-body>
        <!-- Tableau principal -->
        <b-table
          id="passport-list-table"
          striped
          hover
          :items="shownPassports"
          :fields="passports_fields"
          responsive
        >
          <!-- Slot "vide" -->
          <template #empty="scope">
            <div class="lead text-center py-3">
              {{ scope.emptyText }}
            </div>
          </template>

          <!-- production_sequence -->
          <template #cell(production_sequence)="row">
            {{ row.item.production_sequence + '/' + row.item.production_sequence_total }}
          </template>

          <!-- owners_count -->
          <template #cell(owners_count)="row">
            <b-badge :variant="row.item.owners_count > 0 ? 'danger' : 'success'">
              {{ row.item.owners_count > 0 ? "Owned" : "Free" }}
            </b-badge>
          </template>

          <!-- status -->
          <template #cell(status)="row">
            <b-badge :variant="getStatusBadge(row.item.status)">
              {{ row.item.status | capitalize }}
            </b-badge>
          </template>

          <!-- actions -->
          <template #cell(actions)="row">
            <b-button
              variant="outline-primary"
              size="sm"
              @click="viewPassportContent(row.item)"
              class="mr-2"
            >
              View
            </b-button>

            <b-button
              v-if="
                !isRevoked(row.item) &&
                row.item.blockchain_status &&
                (($can('manage passports suspension') && row.item.client_id == client_id) ||
                  $can('manage all passports suspension'))
              "
              variant="outline-primary"
              size="sm"
              @click="changePassportStatus(row.item)"
              class="mr-2"
            >
              {{ getStatusAction(row.item) }}
            </b-button>

            <b-button
              v-if="
                !isRevoked(row.item) &&
                row.item.blockchain_status &&
                (($can('manage passports revocation') && row.item.client_id == client_id) ||
                  $can('manage all passports revocation'))
              "
              variant="outline-danger"
              size="sm"
              @click="revokePassport(row.item)"
              class="mr-2"
            >
              Revoke
            </b-button>

            <b-button
              v-if="
                isActive(row.item) &&
                row.item.blockchain_status &&
                (($can('manage passports details') && row.item.client_id == client_id) ||
                  $can('manage all passports details'))
              "
              variant="outline-primary"
              size="sm"
              @click="addPassportDetails(row.item)"
              class="mr-2"
            >
              Add Details
            </b-button>

            <b-button
              v-if="isActive(row.item)"
              variant="outline-primary"
              size="sm"
              @click="addPassportOwner(row.item)"
              class="mr-2"
            >
              Add Owner
            </b-button>

            <b-button
              v-if="row.item.status === 'pending activation'"
              variant="outline-primary"
              size="sm"
              @click="activatePassport(row.item)"
              class="mr-2"
            >
              Activate
            </b-button>
          </template>
        </b-table>

        <!-- PAGINATION (externe) -->
        <pagination
          :data="passports"
          :limit="4"
          @pagination-change-page="reloadPassports"
        />
      </b-card-body>

      <!-- FOOTER -->
      <b-card-footer>
        <div class="text-center" v-if="passports.total > 1">
          {{ "Showing " + (passports.to - passports.from + 1) + " results out of " + passports.total }}
          <span v-if="isFiltered">
            · {{ $t("results_filtered") }} ·
            <b-link @click="resetFiltering()">
              {{ $t("clear_all") }}
            </b-link>
          </span>
        </div>
      </b-card-footer>
    </b-card>

        <!-- Modales en dehors du <b-card> -->
        <!-- Modal error -->
        <b-modal
            id="modal-error"
            size="sm"
            title="Something went wrong :("
            hide-footer
        >
            <template>
                <div class="d-flex justify-content-between flex-column">
                    <h6>{{ errorMessage }}</h6>
                </div>
            </template>
        </b-modal>

        <!-- Modal owner success -->
        <b-modal id="modal-owner-success" size="sm" title="Success" hide-footer>
            <template>
                <div class="d-flex justify-content-between flex-column">
                    <h6>{{ successMessage }}</h6>
                </div>
            </template>
        </b-modal>

        <!-- Modal to display passport content -->
        <b-modal
            id="modal-content"
            title="Passport"
            size="lg"
            ok-only
            scrollable
            @hide="cancelEditingBlockchain"
        >
            <template>
                <div class="d-flex justify-content-between flex-column">
                    <b-row>
                        <b-col
                            ><h5><strong>Authenticity: </strong></h5></b-col
                        >
                    </b-row>
                    <b-row>
                        <b-col cols="3">Brand:</b-col>
                        <b-col>{{ passportData.client_name }}</b-col>
                    </b-row>
                    <b-row>
                        <b-col cols="3">Collection:</b-col>
                        <b-col>{{ passportData.collection }}</b-col>
                    </b-row>
                    <b-row>
                        <b-col cols="3">Product name:</b-col>
                        <b-col>{{ passportData.base_product_name }}</b-col>
                    </b-row>
                    <b-row>
                        <b-col cols="3">Serial number: </b-col>
                        <b-col>{{ passportData.product_serial_number }}</b-col>
                    </b-row>
                    <b-row
                        v-for="item in passportVariantAttribute"
                        :key="item.id"
                    >
                        <b-col cols="3"
                            >{{ item.name | capitalize }}:</b-col
                        >
                        <b-col>{{ item.value | capitalize }}</b-col>
                    </b-row>
                    <b-row class="mt-3">
                        <b-col
                            ><h5><strong>Origin: </strong></h5></b-col
                        >
                    </b-row>
                    <b-row>
                        <b-col cols="3">Production year:</b-col>
                        <b-col
                            >{{
                                passportData.created_at != undefined &&
                                passportData.created_at.substr(0, 4)
                            }}
                        </b-col>
                    </b-row>
                    <b-row>
                        <b-col cols="3">Production place:</b-col>
                        <b-col>{{ passportData.production_place }}</b-col>
                    </b-row>

                    <b-row class="mt-3">
                        <b-col
                            ><h5><strong>Passport: </strong></h5></b-col
                        >
                    </b-row>
                    <b-row>
                        <b-col cols="3">UUID:</b-col>
                        <b-col>{{ passportData.uuid }}</b-col>
                    </b-row>
                    <b-row>
                        <b-col cols="3">Issued date:</b-col>
                        <b-col>{{
                            passportData.created_at | formatDate("DD-MMM-YYYY")
                        }}</b-col>
                    </b-row>
                    <b-row>
                        <b-col cols="3">Status:</b-col>
                        <b-col>
                            <b-badge
                                :variant="getStatusBadge(passportData.status)"
                            >
                                {{
                                    passportData.status != undefined &&
                                    passportData.status | capitalize
                                }}
                            </b-badge>
                        </b-col>
                    </b-row>

                    <!-- <b-row>
                        <b-col cols="3">Certificate status: </b-col>
                            <b-col>
                            <b-badge :variant="getBlockchainStatusBadge()" >
                                {{ this.certificateStatus != undefined && this.certificateStatus | capitalize }}
                            </b-badge>
                        </b-col>
                    </b-row> -->

                    <!-- Passport blockchain logs -->
                    <div>
                        <b-row class="mt-3">
                            <b-col
                                ><h5>
                                    <strong>Blockchain info: </strong>
                                </h5></b-col
                            >
                        </b-row>
                        <b-row>
                            <b-col cols="3">Blockchain:</b-col>
                            <span
                                v-if="!isEditingBlockchain"
                                style="padding-left: 15px"
                                >{{
                                    passportBlockchainLogs
                                        ? passportBlockchainLogs.blockchain
                                        : " "
                                }}</span
                            >
                            <b-input
                                v-else
                                v-model="editedBlockchain"
                                style="max-width: 400px"
                            ></b-input>
                        </b-row>

                        <b-row>
                            <b-col cols="3">Transaction: </b-col>
                            <span
                                v-if="!isEditingBlockchain"
                                style="padding-left: 15px; margin-right: 25px"
                                >{{
                                    passportBlockchainLogs
                                        ? passportBlockchainLogs.blockchain_log
                                        : " "
                                }}</span
                            >
                            <b-input
                                v-else
                                v-model="editedTransaction"
                                style="max-width: 400px; margin-right: 25px"
                            ></b-input>

                            <b-button
                                v-if="
                                    !isEditingBlockchain &&
                                    $can('manage passports blockchain logs') &&
                                    passportData.status == 'active'
                                "
                                size="sm"
                                variant="outline-primary"
                                @click="startEditingTransaction"
                            >
                                <font-awesome-icon
                                    icon="edit"
                                ></font-awesome-icon>
                                {{ $t("edit") }}</b-button
                            >
                            <b-button
                                v-if="isEditingBlockchain"
                                size="sm"
                                variant="outline-success"
                                @click="saveBlockchainChanges(passportData.id)"
                            >
                                <font-awesome-icon
                                    icon="check"
                                ></font-awesome-icon>
                                {{ $t("save") }}</b-button
                            >

                            <b-button
                                v-if="isEditingBlockchain"
                                size="sm"
                                variant="outline-danger"
                                @click="cancelEditingBlockchain"
                                style="margin-left: 10px"
                            >
                                <font-awesome-icon
                                    icon="times"
                                ></font-awesome-icon>
                                {{ $t("cancel") }}
                            </b-button>
                        </b-row>

                        <b-row>
                            <b-col cols="3">Transaction date:</b-col>
                            <b-col>{{
                                (passportBlockchainLogs &&
                                    formatDateBlockchain(
                                        passportBlockchainLogs.updated_at
                                    )) ||
                                " "
                            }}</b-col>
                        </b-row>
                    </div>

                    <!-- Passport files -->
                    <div v-if="true">
                        <b-row class="mt-3">
                            <b-col
                                ><h5>
                                    <strong>Attached Files: </strong>
                                </h5></b-col
                            >
                        </b-row>
                        <file-manager
                            title=""
                            :resource-id="passportData.id"
                            resource-type="passport"
                        ></file-manager>
                    </div>

                    <!-- Passport details -->
                    <div v-if="passportDetails[0]">
                        <b-row class="mt-3">
                            <b-col
                                ><h5><strong>Details: </strong></h5></b-col
                            >
                        </b-row>
                        <div class="d-flex flex-column" style="margin: 4px">
                            <b-table
                                sticky-header
                                outlined
                                :items="passportDetails"
                                :fields="passports_details_fields"
                            >
                                <!-- Template to display audit reason -->
                                <template #cell(reason)="row">
                                    <span v-if="row.item.status">{{
                                        "Status updated to: " + row.item.status
                                    }}</span>
                                    <span v-else>User comment</span>
                                </template>
                            </b-table>
                        </div>
                    </div>
                </div>
            </template>
        </b-modal>

        <!-- Modal to update passport status -->
        <b-modal
            id="modal-update-status"
            ref="modal"
            :title="'Passport ' + modalTitleAction"
            @show="resetModalStatusUpdate"
            @hidden="resetModalStatusUpdate"
            @ok="handleSubmitStatusUpdate"
        >
            <form ref="form" @submit.stop.prevent="submitStatusUpdate">
                <p>
                    Please provide a reason for passport {{ modalTitleAction }}:
                </p>
                <b-form-group
                    label-for="reason"
                    invalid-feedback="Reason for status update is mandatory"
                    :state="reasonState"
                >
                    <b-form-textarea
                        id="reason"
                        v-model="reason"
                        :state="reasonState"
                        required
                        placeholder="Enter a reason"
                    />
                </b-form-group>
            </form>
        </b-modal>

        <!-- Modal to revoke passport -->
        <b-modal
            id="modal-revocation"
            ref="modal"
            title="Passport revocation"
            @show="resetModalRevocation"
            @hidden="resetModalRevocation"
            @ok="handleSubmitRevocation"
        >
            <form ref="form" @submit.stop.prevent="submitRevocation">
                <b-alert show variant="danger">
                    This operation is <b>irreverisble</b>
                </b-alert>

                <p>Please provide a reason:</p>
                <b-form-group
                    label-for="reason"
                    invalid-feedback="Reason for revocation is mandatory"
                    :state="reasonState"
                >
                    <b-form-textarea
                        id="revocation-reason"
                        v-model="reason"
                        :state="reasonState"
                        required
                        placeholder="Enter a reason for revoking this passport"
                    />
                </b-form-group>
                <p>Please confirm the passport UUID to revoke:</p>
                <b-form-group
                    label-for="uuid"
                    invalid-feedback="UUID does not match"
                    :state="uuidState"
                >
                    <b-form-input
                        id="revocation-uuid"
                        v-model="uuid"
                        :state="uuidState"
                        required
                        placeholder="UUID of passport to revoke"
                    />
                </b-form-group>
            </form>
        </b-modal>

        <!-- Modal to add passport details -->
        <b-modal
            id="modal-details"
            ref="modal"
            title="Add details"
            @show="resetModalDetails"
            @hidden="resetModalDetails"
            @ok="handleSubmitDetails"
        >
            <form ref="form" @submit.stop.prevent="submitDetails">
                <p>Here you can add a passport's details:</p>
                <b-form-group
                    label-for="details"
                    invalid-feedback="Enter something"
                    :state="detailsState"
                >
                    <b-form-textarea
                        id="details"
                        v-model="details"
                        :state="detailsState"
                        required
                    />
                </b-form-group>
            </form>
        </b-modal>

        <!-- Modal add owner -->
        <b-modal
            id="modal-owner"
            ref="modal"
            title="Add owner"
            @show="resetModalOwner"
            @hidden="resetModalOwner"
            @ok="handleSubmitOwner"
        >
            <form ref="form" @submit.stop.prevent="submitOwner">
                <p>Here you can add a passport's owner:</p>

                <b-form-group
                    :state="ownerState"
                    label-for="details"
                    invalid-feedback="You need to enter a valid email address"
                >
                    <b-form-input
                        id="email"
                        v-model="email"
                        autocomplete="off"
                        @input="autocompleteUser"
                        required
                        placeholder="Email address"
                        class="my-2"
                    />
                    <div
                        class="autocomplete-items"
                        v-if="autocompleteItems.length > 0"
                    >
                        <ul>
                            <li
                                v-for="user in autocompleteItems"
                                :key="user.id"
                                @click="setAutocompleteUser(user)"
                            >
                                {{ user.email }}
                            </li>
                        </ul>
                    </div>
                    <b-form-input
                        id="firstname"
                        v-model="firstname"
                        required
                        placeholder="Firstname"
                        class="my-2"
                    />
                    <b-form-input
                        id="lastname"
                        v-model="lastname"
                        required
                        placeholder="Lastname"
                        class="my-2"
                    />
                    <b-form-input
                        id="address"
                        v-model="address"
                        placeholder="Address"
                        class="my-2"
                    />
                    <b-form-input
                        id="phone"
                        v-model="phone"
                        placeholder="Phone number"
                        class="my-2"
                    />
                </b-form-group>
            </form>
        </b-modal>
  </div>
</template>

<script>
import axios from "axios";
import moment from "moment";
import { mapActions } from "vuex";
import FileManager from "../Common/FileManager";

export default {
  name: "PassportList",
  components: { FileManager },
  props: {
    passports: { type: Object, required: true },
    clients: { type: Array, required: true },
    manufacturers: { type: Array, required: true },
    client_id: { type: Number, required: true },
    is_show_clients: { type: Boolean, default: true },
    is_show_manufacturers: { type: Boolean, default: true },
    // pour stocker la recherche initiale
    search: { type: String, default: "" },
    status: { type: String, default: "" },
    owner_status: { type: String, default: "" },
    search_client: { type: String, default: null },
    search_manufacturer: { type: Number, default: null }
  },
  data() {
    return {
      userInfoLoaded: false,
      isLoading: false,
      errorMessage: "",
      successMessage: null,
      autocompleteItems: [],

      shownPassports: this.passports.data,

      // Champs du tableau
      passports_fields: [
        { key: "uuid", label: "UUID", sortable: false },
        {
          key: "created_at",
          label: "Date",
          sortable: true,
          formatter: value => this.formatDateAssigned(value, "YYYY-MM-DD")
        },
        { key: "reference", label: "Reference", sortable: false },
        {
          key: "product_serial_number",
          label: "Serial Number",
          sortable: false
        },
        {
          key: "production_sequence",
          label: "Production sequence",
          sortable: true
        },
        { key: "client_name", label: "Client", sortable: false },
        { key: "manufacturer_name", label: "Manufacturer", sortable: false },
        { key: "owners_count", label: "Owner Status", sortable: true },
        { key: "status", label: "Status", sortable: true },
        { key: "actions", label: "Actions", sortable: false }
      ],

      // Filtres
      searchText: this.search,
      searchStatus: this.status,
      searchOwnerStatus: this.owner_status,
      searchClient: this.search_client,
      searchManufacturer: this.search_manufacturer,
      showFilters: false, // Bascule l'affichage du bloc "advanced search"

      // Données modales / logiques ...
      currentPassport: {},
      passportData: {},
      passportBlockchainLogs: {},
      passportDetails: [],
      passportVariantAttribute: [],
      certificates: [],
      uuid: "",
      uuidState: false,
      reason: "",
      reasonState: false,
      details: "",
      firstname: "",
      lastname: "",
      address: "",
      phone: "",
      email: "",
      detailsState: false,
      ownerState: false,
      certificateStatus: "invalid",
      isEditingBlockchain: false,
      editedBlockchain: "",
      editedTransaction: "",
      originalBlockchain: "",
      originalTransaction: ""
    };
  },
  computed: {
    isFiltered() {
      return (
        this.searchText ||
        this.searchStatus ||
        this.searchOwnerStatus ||
        this.searchClient ||
        this.searchManufacturer
      );
    },
    modalTitleAction() {
      let s = (this.currentPassport.status || "").toLowerCase();
      if (s === "active") return "suspension";
      if (s === "suspended") return "activation";
      return "unknown status (" + s + ")";
    }
  },
  mounted() {
    this.getMe().then(() => {
      this.userInfoLoaded = true;
    });
  },
  watch: {
    // A la manière du ReportList, on surveille les filtres
    // et on relance applyFilters() après un petit délai
    searchText: {
      handler: _.debounce(function() {
        this.applyFilters();
      }, 500),
      deep: true
    },
    searchStatus: {
      handler: _.debounce(function() {
        this.applyFilters();
      }, 500),
      deep: true
    },
    searchOwnerStatus: {
      handler: _.debounce(function() {
        this.applyFilters();
      }, 500),
      deep: true
    },
    searchClient: {
      handler: _.debounce(function() {
        this.applyFilters();
      }, 500),
      deep: true
    },
    searchManufacturer: {
      handler: _.debounce(function() {
        this.applyFilters();
      }, 500),
      deep: true
    }
  },
  methods: {
    ...mapActions("me", ["getMe"]),

    autocompleteUser(searchString) {
      this.autocompleteItems = [];
      if (searchString.length >= 3) {
        axios
          .get("/api/passport-owners?query=" + searchString)
          .then(response => {
            this.autocompleteItems = response.data.data;
          });
      }
    },

    setAutocompleteUser(user) {
      this.autocompleteItems = [];
      this.email = user.email;
      this.firstname = user.firstname;
      this.lastname = user.lastname;
      this.phone = user.phone;
      this.address = user.address;
    },

    formatDateAssigned(value, fmt) {
      if (!value) return "";
      return moment(String(value)).format(fmt);
    },
    formatDateBlockchain(date) {
      if (!date) return "";
      const options = { year: "numeric", month: "short", day: "2-digit" };
      return new Date(date).toLocaleDateString("en-US", options);
    },
    // Helper badge
    getStatusBadge(status) {
      if (!status) return "secondary";
      status = status.toLowerCase();
      if (status === "active") return "success";
      if (status === "pending activation" || status === "suspended")
        return "warning";
      return "danger";
    },
    isActive(item) {
      return item && item.status.toLowerCase() === "active";
    },
    isRevoked(item) {
      return item && item.status.toLowerCase() === "revoked";
    },
    getStatusAction(item) {
      if (!item || !item.status) return "";
      const s = item.status.toLowerCase();
      if (s === "active") return "Suspend";
      if (s === "suspended") return "Activate";
      return "";
    },

    // Filtres
    applyFilters() {
      // On supprime le bouton "Filter passports",
      // donc c'est la watch (debounce) qui appelle applyFilters() à chaque modif
      let url = this.passports.path + "?page=1";
      if (this.searchText) url += "&search=" + this.searchText;
      if (this.searchStatus) url += "&status=" + this.searchStatus;
      if (this.searchOwnerStatus)
        url += "&owner_status=" + this.searchOwnerStatus;
      if (this.searchClient !== null) url += "&client=" + this.searchClient;
      if (this.searchManufacturer !== null)
        url += "&manufacturer=" + this.searchManufacturer;

      window.location.href = url;
    },
    resetFiltering() {
      this.searchText = "";
      this.searchStatus = "";
      this.searchOwnerStatus = "";
      this.searchClient = null;
      this.searchManufacturer = null;
    },

    // Pagination (via composant <pagination>)
    reloadPassports(page) {
      let url = this.passports.path + "?page=" + page;
      if (this.searchText) url += "&search=" + this.searchText;
      if (this.searchStatus) url += "&status=" + this.searchStatus;
      if (this.searchOwnerStatus)
        url += "&owner_status=" + this.searchOwnerStatus;
      if (this.searchClient) url += "&client=" + this.searchClient;
      if (this.searchManufacturer)
        url += "&manufacturer=" + this.searchManufacturer;

      window.location.href = url;
    },

    // Exemples d'actions
    viewPassportContent(passport) {
      axios
        .get("/api/passports/" + passport.id)
        .then(res => {
          this.currentPassport = passport;
          this.passportData = res.data.passport_data;
          this.passportDetails = res.data.passport_details;
          this.passportVariantAttribute = res.data.passport_variant_attribute;
          this.certificates = res.data.certificates;
          this.passportBlockchainLogs = res.data.passport_blockchain_logs;
          this.$bvModal.show("modal-content");
        })
        .catch(err => {
          this.errorMessage = err;
          this.$bvModal.show("modal-error");
        });
    },
    changePassportStatus(passport) {
      this.currentPassport = passport;
      this.$bvModal.show("modal-update-status");
    },
    revokePassport(passport) {
      this.currentPassport = passport;
      this.$bvModal.show("modal-revocation");
    },
    activatePassport(passport) {
      axios
        .post("/api/passports/" + passport.id + "/activate")
        .then(response => {
          let updated = this.shownPassports.find(
            p => p.id === response.data.data.id
          );
          if (updated) updated.status = response.data.data.status;
        });
    },
    addPassportDetails(passport) {
      this.currentPassport = passport;
      this.$bvModal.show("modal-details");
    },
    addPassportOwner(passport) {
      this.currentPassport = passport;
      this.$bvModal.show("modal-owner");
    },

    // Gestions du "status update" modal
    resetModalStatusUpdate() {
      this.reason = "";
      this.reasonState = null;
    },
    handleSubmitStatusUpdate(bvModalEvt) {
      bvModalEvt.preventDefault();
      this.submitStatusUpdate();
    },
    isStatusUpdateFormValid() {
      const valid = this.$refs.form && this.$refs.form.checkValidity();
      this.reasonState = this.reason.length > 0;
      return valid && this.reasonState;
    },
    submitStatusUpdate() {
      if (!this.isStatusUpdateFormValid()) return;
      const action = this.isActive(this.currentPassport)
        ? "suspend"
        : "activate";
      this.changeStatus(this.currentPassport, action, this.reason);

      this.$nextTick(() => {
        this.$bvModal.hide("modal-update-status");
      });
    },

    // Gestions du "revocation" modal
    resetModalRevocation() {
      this.reason = "";
      this.reasonState = null;
      this.uuid = "";
      this.uuidState = null;
    },
    handleSubmitRevocation(bvModalEvt) {
      bvModalEvt.preventDefault();
      this.submitRevocation();
    },
    isRevocationFormValid() {
      const form_valid = this.$refs.form && this.$refs.form.checkValidity();
      this.reasonState = this.reason.length > 0;
      this.uuidState = this.currentPassport.uuid === this.uuid;
      return form_valid && this.reasonState && this.uuidState;
    },
    submitRevocation() {
      if (!this.isRevocationFormValid()) return;
      this.changeStatus(this.currentPassport, "revoke", this.reason);
      this.$nextTick(() => {
        this.$bvModal.hide("modal-revocation");
      });
    },

    // Add details
    resetModalDetails() {
      this.details = "";
      this.detailsState = null;
    },
    handleSubmitDetails(bvModalEvt) {
      bvModalEvt.preventDefault();
      this.submitDetails();
    },
    isAddDetailsFormValid() {
      const valid = this.$refs.form && this.$refs.form.checkValidity();
      this.detailsState = this.details.length > 0;
      return valid;
    },
    submitDetails() {
      if (!this.isAddDetailsFormValid()) return;
      axios
        .put(`/api/passports/${this.currentPassport.id}/details`, {
          detail: this.details
        })
        .catch(err => {
          this.errorMessage = err;
          this.$bvModal.show("modal-error");
        });
      this.$nextTick(() => {
        this.$bvModal.hide("modal-details");
      });
    },

    // Add owner
    resetModalOwner() {
      this.firstname = "";
      this.lastname = "";
      this.address = "";
      this.phone = "";
      this.email = "";
      this.ownerState = null;
    },
    handleSubmitOwner(bvModalEvt) {
      bvModalEvt.preventDefault();
      this.submitOwner();
    },
    isAddOwnerFormValid() {
      const valid = this.$refs.form && this.$refs.form.checkValidity();
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      this.ownerState = emailRegex.test(this.email);
      return valid && this.ownerState;
    },
    submitOwner() {
      if (!this.isAddOwnerFormValid()) return;
      let message =
        "You are about assigning a passport to the user : " +
        this.email +
        ". Please confirm to continue";
      if (
        this.currentPassport.owners_count !== undefined &&
        this.currentPassport.owners_count > 0
      ) {
        message =
          "This passport is already owned, do you really want to change this owner?";
      }
      this.$bvModal
        .msgBoxConfirm(message, {
          title: "Do you want to continue?",
          size: "md",
          buttonSize: "md",
          okVariant: "primary",
          okTitle: "Yes",
          footerClass: "p-2",
          hideHeaderClose: false
        })
        .then(value => {
          if (value) {
            this.isLoading = true;
            axios
              .put(`/api/passports/${this.currentPassport.id}/owner`, {
                firstname: this.firstname,
                lastname: this.lastname,
                address: this.address,
                phone: this.phone,
                email: this.email
              })
              .then(response => {
                this.shownPassports.forEach(pass => {
                  if (pass.id === response.data.data.id) {
                    pass.owners_count++;
                  }
                });
                this.isLoading = false;
                this.successMessage =
                  "The passport is now owned by " +
                  response.data.data.owner.email +
                  ".";
                this.$bvModal.show("modal-owner-success");
              })
              .catch(err => {
                this.isLoading = false;
                if (err.response && err.response.status === 422) {
                  err = Object.values(err.response.data.errors)
                    .flat()
                    .join("\r\n");
                }
                this.errorMessage = err;
                this.$bvModal.show("modal-error");
              });
            this.$nextTick(() => {
              this.$bvModal.hide("modal-owner");
            });
          }
        });
    },

    // Méthode pour réellement changer le status (activate, suspend, revoke)
    changeStatus(passport, action, reason) {
      if (!["activate", "suspend", "revoke"].includes(action.toLowerCase())) {
        this.errorMessage = "Invalid '" + action + "' operation.";
        this.$bvModal.show("modal-error");
        return;
      }
      axios
        .put(`/api/passports/${passport.id}/${action}`, { reason })
        .then(res => {
          passport.status = res.data.status;
        })
        .catch(err => {
          this.errorMessage = err;
          this.$bvModal.show("modal-error");
        });
    },

    // Blockchain editing
    cancelEditingBlockchain() {
      if (this.passportBlockchainLogs) {
        this.passportBlockchainLogs.blockchain = this.originalBlockchain;
        this.passportBlockchainLogs.blockchain_log = this.originalTransaction;
      }
      this.isEditingBlockchain = false;
      this.editedBlockchain = "";
      this.editedTransaction = "";
      this.originalBlockchain = "";
      this.originalTransaction = "";
    },
    async saveBlockchainChanges(passportID) {
      const response = await axios.put(
        `/api/passports/${passportID}/blockchainLog`,
        {
          blockchain: this.editedBlockchain,
          blockchain_log: this.editedTransaction
        }
      );
      if (response.status === 200) {
        this.isEditingBlockchain = false;
        if (!this.passportBlockchainLogs) {
          this.passportBlockchainLogs = {};
        }
        this.passportBlockchainLogs.blockchain = this.editedBlockchain;
        this.passportBlockchainLogs.blockchain_log = this.editedTransaction;
        this.passportBlockchainLogs.updated_at = new Date().toISOString();
        this.editedBlockchain = "";
        this.editedTransaction = "";
        this.originalBlockchain = "";
        this.originalTransaction = "";
        this.$bvToast.toast(this.$t("blockchain_update_success"), {
          title: this.$t("blockchain_title_update_success"),
          toaster: "b-toaster-top-right",
          autoHideDelay: 5000,
          appendToast: true,
          solid: true,
          variant: "success"
        });
      } else {
        this.$bvToast.toast(this.$t("blockchain_update_fail"), {
          title: this.$t("blockchain_title_update_fail"),
          toaster: "b-toaster-top-right",
          autoHideDelay: 5000,
          appendToast: true,
          solid: true,
          variant: "danger"
        });
      }
    },
    startEditingTransaction() {
      this.isEditingBlockchain = true;
      if (this.passportBlockchainLogs) {
        this.editedBlockchain = this.passportBlockchainLogs.blockchain || "";
        this.editedTransaction =
          this.passportBlockchainLogs.blockchain_log || "";
        this.originalBlockchain = this.editedBlockchain;
        this.originalTransaction = this.editedTransaction;
      } else {
        this.editedBlockchain = "";
        this.editedTransaction = "";
        this.originalBlockchain = "";
        this.originalTransaction = "";
      }
    }
  },
  filters: {
    capitalize(value) {
      if (!value) return "";
      value = value.toString();
      return value.charAt(0).toUpperCase() + value.slice(1);
    },
    formatDate(value, fmt) {
      if (!value) return "";
      return moment(String(value)).format(fmt);
    }
  }
};
</script>

<style scoped lang="scss">
.card-container {
  margin-bottom: 1rem;
}

/* Bloc "advanced search" */
.reports-filters {
  border: 1px solid #ddd;
  border-radius: 4px;
}

#app .b-table .sr-only {
  display: none;
}

.spanner {
  position: absolute;
  top: 50%;
  left: 0;
  background: rgba(0, 0, 0, 0.65);
  width: 100%;
  text-align: center;
  height: 300px;
  color: #fff;
  transform: translateY(-50%);
  z-index: 1000;
}

.overlay {
  position: fixed;
  width: 100%;
  height: 100%;
  visibility: hidden;
}

.loader,
.loader:before,
.loader:after {
  border-radius: 50%;
  width: 2.5em;
  height: 2.5em;
  animation-fill-mode: both;
  animation: load7 1.8s infinite ease-in-out;
}

.loader {
  color: #ffffff;
  font-size: 10px;
  margin: 80px auto;
  position: relative;
  text-indent: -9999em;
  transform: translateZ(0);
  animation-delay: -0.16s;
}

.loader:before,
.loader:after {
  content: "";
  position: absolute;
  top: 0;
}

.loader:before {
  left: -3.5em;
  animation-delay: -0.32s;
}

.loader:after {
  left: 3.5em;
}

@keyframes load7 {
  0%,
  80%,
  100% {
    box-shadow: 0 2.5em 0 -1.3em;
  }
  40% {
    box-shadow: 0 2.5em 0 0;
  }
}

.show {
  visibility: visible;
}

.spanner,
.overlay {
  opacity: 0;
  transition: all 0.3s;
}

.spanner.show,
.overlay.show {
  opacity: 1;
}

.autocomplete-items {
  border: 1px solid #ced4da;
  border-radius: 0.25rem;
}

.autocomplete-items ul {
  margin: 0;
  padding: 10px 10px 10px 7px;
}

.autocomplete-items ul li {
  list-style-type: none;
  cursor: pointer;
  padding: 3px;
}

.autocomplete-items ul li:hover {
  background: rgba(197, 197, 197, 0.62);
}
</style>
